import React, { useState } from 'react';
import { Tabs, Spin } from 'antd';
import ConditionPricingQueryTab from './components/ConditionPricingQueryTab';
import TrackingNumberPricingQueryTab from './components/TrackingNumberPricingQueryTab';
import { usePricingQuery } from './hooks/usePricingQuery';
import { PricingResultType } from './graphql/mutations/pricingQueryMutations';

const { TabPane } = Tabs;

const BATCH_PAGES = 100;

const PricingQueryPage: React.FC = () => {
    const { queryByCondition, queryByTrackingNumber, loading: queryLoading } = usePricingQuery();

    const [conditionQueryResult, setConditionQueryResult] = useState<{
        data: PricingResultType[];
        totalCount: number;
        nextCursor: string | null;
    } | null>(null);
    const [conditionLastInput, setConditionLastInput] = useState<{
        clientIds?: number[] | null;
        businessAtStart?: string | null;
        businessAtEnd?: string | null;
        pricingErrorCode?: string | null;
        pricingNodeCode?: string | null;
        businessScenarioCode?: number | null;
        isSuccess?: boolean | null;
        pageSize: number;
    } | null>(null);
    const [trackingQueryResult, setTrackingQueryResult] = useState<PricingResultType[] | null>(null);
    const [conditionPage, setConditionPage] = useState(1);
    const [conditionPageSize, setConditionPageSize] = useState(20);
    const [activeQueryTab, setActiveQueryTab] = useState('condition-query');

    const handleConditionQuery = async (input: {
        clientIds?: number[] | null;
        businessAtStart?: string | null;
        businessAtEnd?: string | null;
        pricingErrorCode?: string | null;
        pricingNodeCode?: string | null;
        businessScenarioCode?: number | null;
        isSuccess?: boolean | null;
        page: number;
        pageSize: number;
        cursor?: string | null;
    }) => {
        if (!input.cursor) {
            setConditionQueryResult(null);
        }
        const result = await queryByCondition.executeQueryPricingByCondition({
            clientIds: input.clientIds ?? null,
            businessAtStart: input.businessAtStart ?? null,
            businessAtEnd: input.businessAtEnd ?? null,
            pricingErrorCode: input.pricingErrorCode ?? null,
            pricingNodeCode: input.pricingNodeCode ?? null,
            businessScenarioCode: input.businessScenarioCode ?? null,
            isSuccess: input.isSuccess ?? null,
            page: input.page,
            pageSize: input.pageSize,
            cursor: input.cursor ?? null,
            batchPages: BATCH_PAGES
        });
        if (result?.success) {
            if (input.cursor && conditionQueryResult) {
                setConditionQueryResult({
                    data: conditionQueryResult.data.concat(result.data),
                    totalCount: conditionQueryResult.totalCount,
                    nextCursor: result.nextCursor ?? null
                });
            } else {
                setConditionLastInput({
                    clientIds: input.clientIds ?? null,
                    businessAtStart: input.businessAtStart ?? null,
                    businessAtEnd: input.businessAtEnd ?? null,
                    pricingErrorCode: input.pricingErrorCode ?? null,
                    pricingNodeCode: input.pricingNodeCode ?? null,
                    businessScenarioCode: input.businessScenarioCode ?? null,
                    isSuccess: input.isSuccess ?? null,
                    pageSize: input.pageSize
                });
                setConditionQueryResult({
                    data: result.data,
                    totalCount: result.totalCount,
                    nextCursor: result.nextCursor ?? null
                });
            }
        } else if (!input.cursor) {
            setConditionQueryResult(null);
        }
    };

    const handleLoadNextBatch = async (cursor: string) => {
        if (!conditionLastInput) return;
        await handleConditionQuery({
            ...conditionLastInput,
            page: 1,
            pageSize: conditionLastInput.pageSize,
            cursor
        });
    };

    const handleTrackingNumberQuery = async (trackingNumber: string, pricingNodeCode?: string | null) => {
        setTrackingQueryResult(null);
        const result = await queryByTrackingNumber.executeQueryPricingByTrackingNumber({
            trackingNumber,
            pricingNodeCode: pricingNodeCode || null
        });
        if (result?.success) {
            setTrackingQueryResult(result.data);
        } else {
            setTrackingQueryResult(null);
        }
    };

    const handleViewDetail = (trackingNumber: string) => {
        setActiveQueryTab('tracking-query');
        handleTrackingNumberQuery(trackingNumber);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">计费查询</h1>
                <p className="text-gray-600 mt-2">按条件分页查询计费结果，或按运单号查询详情</p>
            </div>

            <Tabs activeKey={activeQueryTab} onChange={setActiveQueryTab} type="card">
                <TabPane tab="按条件查询" key="condition-query">
                    <Spin spinning={queryLoading} tip="正在查询计费信息，请稍候...">
                        <ConditionPricingQueryTab
                            onQuery={handleConditionQuery}
                            onLoadNextBatch={handleLoadNextBatch}
                            onViewDetail={handleViewDetail}
                            loading={queryLoading}
                            queryResult={conditionQueryResult}
                            page={conditionPage}
                            pageSize={conditionPageSize}
                            onPageChange={(p, size) => {
                                setConditionPage(p);
                                setConditionPageSize(size);
                            }}
                        />
                    </Spin>
                </TabPane>
                <TabPane tab="按运单号查询" key="tracking-query">
                    <Spin spinning={queryLoading} tip="正在查询计费信息，请稍候...">
                        <TrackingNumberPricingQueryTab
                            onQuery={handleTrackingNumberQuery}
                            loading={queryLoading}
                            queryResult={trackingQueryResult}
                        />
                    </Spin>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default PricingQueryPage;
